import * as React from "react"
import type { ReactElement } from "react"
import { Building2 } from "lucide-react"
import { useTheme } from "next-themes"

import { cn } from "@/lib/utils"
import { resolveTradingViewLaneBadge } from "@/utils/tradingViewLaneUi"
import { resolveBrokerIconLogoUrl } from "./brokerIconLogos"

const brokerMarkFrameStrokeClass =
  "rounded-[6px] border border-solid border-border p-0.5 !shadow-none ring-0"

const brokerMarkFrameClass = cn(brokerMarkFrameStrokeClass, "bg-background")

export type BrokerMarkProps = {
  brokerName: string
  displayName: string
  className?: string
}

export function BrokerMark({
  brokerName,
  displayName,
  className,
}: BrokerMarkProps): ReactElement {
  const { resolvedTheme } = useTheme()
  const logoUrl = resolveBrokerIconLogoUrl(brokerName, {
    dark: resolvedTheme === "dark",
  })
  const laneBadge = resolveTradingViewLaneBadge(brokerName)
  const laneMark = laneBadge === "Paper" ? "P" : laneBadge === "Backtest" ? "B" : null

  if (logoUrl) {
    return (
      <div
        data-broker-mark-frame
        data-broker-lane={laneBadge ?? undefined}
        className={cn(
          "relative flex aspect-square shrink-0 items-center justify-center overflow-hidden",
          brokerMarkFrameClass,
          className
        )}
      >
        <img
          src={logoUrl}
          alt={displayName}
          className="size-full max-h-full max-w-full object-contain"
          decoding="async"
        />
        {laneMark ? (
          <span
            data-broker-lane-mark=""
            title={laneBadge ?? undefined}
            aria-hidden
            className="absolute bottom-0 right-0 flex size-3.5 items-center justify-center rounded-tl-[4px] bg-[color:var(--brand-tropical-surf)] font-[family-name:var(--font-primary)] text-[8px] font-semibold leading-none text-white"
          >
            {laneMark}
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <div
      data-broker-mark-frame
      className={cn(
        "flex aspect-square shrink-0 items-center justify-center overflow-hidden",
        brokerMarkFrameClass,
        className
      )}
    >
      <Building2 className="size-4 text-muted-foreground" aria-hidden />
    </div>
  )
}
